import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { useSearchParams, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { io } from 'socket.io-client';
import { motion } from 'framer-motion';
import {
  RadioButtonChecked,
  ContentCopy,
  Check,
  Link as LinkIcon,
} from '@mui/icons-material';
import QoreTalksShell from './QoreTalksShell';
import LiveRoomCard from './LiveRoomCard';
import QtEmptyState from './QtEmptyState';
import VideoTile from './VideoTile';
import LiveStudio from './LiveStudio';
import './QoreTalks.css';
import './LiveStudio.css';
import { SOCKET_URL } from '../../config/api';
import { useAuth } from '../../context/AuthContext';
import { fetchLiveRooms, fetchLiveRoomInfo, createLiveMeeting } from '../../services/qoreTalksApi';
import { USE_MOCKS } from '../../constants/qoreTalks';

// ─── constants ────────────────────────────────────────────────────────────────
const ICE_SERVERS = [{ urls: 'stun:stun.l.google.com:19302' }];

// ─── component ────────────────────────────────────────────────────────────────
const LiveShow = () => {
  const { t } = useTranslation();
  const { user, token, isAdmin, isInstructor } = useAuth();
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const roomIdParam = (params.roomId || searchParams.get('roomId') || '').toUpperCase();
  const createMode = searchParams.get('create') === 'true';
  const isHostParam = searchParams.get('host') === '1';

  // phase: browse | create | precall | live
  const [phase, setPhase] = useState('browse');
  const [liveRooms, setLiveRooms] = useState([]);
  const [roomsLoading, setRoomsLoading] = useState(false);
  const [roomInfo, setRoomInfo] = useState(null);
  const [roomParticipants, setRoomParticipants] = useState([]);

  const [localStream, setLocalStream] = useState(null);
  const [remoteStreams, setRemoteStreams] = useState(new Map());
  const [isMicOn, setIsMicOn] = useState(true);
  const [isCameraOn, setIsCameraOn] = useState(true);

  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [unreadChat, setUnreadChat] = useState(0);
  const [isSharing, setIsSharing] = useState(false);
  const [toast, setToast] = useState('');
  const [incomingReaction, setIncomingReaction] = useState(null);
  const [devices, setDevices] = useState({ audio: [], video: [] });
  const [selectedDevices, setSelectedDevices] = useState({ audio: '', video: '' });
  const chatEndRef = useRef(null);
  const cameraTrackRef = useRef(null);
  const screenStreamRef = useRef(null);

  const [handRaised, setHandRaised] = useState(false);
  const [raisedHands, setRaisedHands] = useState({});
  const [createForm, setCreateForm] = useState({ title: '', description: '' });
  const [createLoading, setCreateLoading] = useState(false);
  const [joinError, setJoinError] = useState('');
  const [guestName, setGuestName] = useState(() => sessionStorage.getItem('qtGuestName') || '');
  const [copied, setCopied] = useState('');
  const [meetingEnded, setMeetingEnded] = useState(false);

  const socketRef = useRef(null);
  const peersRef = useRef(new Map());
  const localStreamRef = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  useEffect(() => {
    if (roomIdParam) {
      fetchRoomInfo(roomIdParam);
    } else if (createMode && (isAdmin || isInstructor)) {
      setPhase('create');
    } else {
      fetchLiveRoomsList();
    }
    return () => cleanup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomIdParam]);

  const fetchLiveRoomsList = async () => {
    setRoomsLoading(true);
    try {
      const rooms = await fetchLiveRooms();
      setLiveRooms(rooms.length > 0 ? rooms : (USE_MOCKS ? DEMO_ROOMS : []));
    } catch {
      setLiveRooms(USE_MOCKS ? DEMO_ROOMS : []);
    } finally {
      setRoomsLoading(false);
      setPhase('browse');
    }
  };

  const meetingShareUrl = (id) => `${window.location.origin}/qoretalks/live/${(id || roomIdParam || roomInfo?.roomId || '').toUpperCase()}`;

  const copyText = async (value, key) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(key);
      setToast(key === 'id' ? 'Meeting ID copied' : 'Invite link copied');
      setTimeout(() => { setCopied(''); setToast(''); }, 1800);
    } catch {
      setJoinError('Could not copy — select the link and copy it manually.');
    }
  };

  const replacePeerTrack = (kind, track) => {
    peersRef.current.forEach((pc) => {
      const sender = pc.getSenders().find((s) => s.track && s.track.kind === kind);
      if (sender && track) sender.replaceTrack(track);
    });
  };

  const refreshDevices = async () => {
    try {
      const list = await navigator.mediaDevices.enumerateDevices();
      setDevices({
        audio: list.filter((d) => d.kind === 'audioinput'),
        video: list.filter((d) => d.kind === 'videoinput'),
      });
    } catch { /* ignore */ }
  };

  const fetchRoomInfo = async (id) => {
    try {
      const data = await fetchLiveRoomInfo(id, token);
      if (!data) {
        setMeetingEnded(true);
        setRoomInfo({ _id: id, roomId: id, title: 'Meeting', status: 'ended' });
        setPhase('ended');
        return;
      }
      if (data.status === 'ended') {
        setMeetingEnded(true);
        setRoomInfo(data);
        setPhase('ended');
        return;
      }
      setRoomInfo(data);
    } catch {
      setRoomInfo({ _id: id, roomId: id, title: 'Live Room', host: { name: 'Host' }, participantCount: 0 });
    }
    startPrecall();
  };

  const startPrecall = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      localStreamRef.current = stream;
      cameraTrackRef.current = stream.getVideoTracks()[0] || null;
      setLocalStream(stream);
      const audioId = stream.getAudioTracks()[0]?.getSettings?.().deviceId || '';
      const videoId = stream.getVideoTracks()[0]?.getSettings?.().deviceId || '';
      setSelectedDevices({ audio: audioId, video: videoId });
      await refreshDevices();
    } catch (err) {
      console.warn('Camera/mic unavailable:', err.message);
    }
    setPhase('precall');
  };

  const createPeer = useCallback((remoteSocketId) => {
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
    const ls = localStreamRef.current;
    if (ls) ls.getTracks().forEach(t => pc.addTrack(t, ls));

    pc.ontrack = (ev) => {
      setRemoteStreams(prev => {
        const next = new Map(prev);
        next.set(remoteSocketId, ev.streams[0]);
        return next;
      });
    };

    pc.onicecandidate = (ev) => {
      if (ev.candidate && socketRef.current) {
        const id = roomIdParam || roomInfo?.roomId;
        socketRef.current.emit('webrtc-ice-candidate', {
          roomId: id,
          to: remoteSocketId,
          targetSocketId: remoteSocketId,
          candidate: ev.candidate,
        });
      }
    };

    peersRef.current.set(remoteSocketId, pc);
    return pc;
  }, []);

  const joinRoom = async () => {
    const targetId = (roomIdParam || roomInfo?._id || roomInfo?.roomId || '').toUpperCase();
    if (!targetId) return;
    if (!token && !guestName.trim()) {
      setJoinError('Enter your name to join this meeting.');
      return;
    }
    if (guestName.trim()) sessionStorage.setItem('qtGuestName', guestName.trim());

    const socket = io(`${SOCKET_URL}/live`, {
      auth: token ? { token } : { guestName: guestName.trim() },
      transports: ['websocket', 'polling'],
    });
    socketRef.current = socket;
    const asHost = isHostParam || isAdmin || isInstructor;

    socket.on('connect', () => {
      if (asHost && (isAdmin || isInstructor)) {
        socket.emit('create-room', { roomId: targetId, title: roomInfo?.title, description: roomInfo?.description });
        socket.once('room-created', () => {
          socket.emit('join-room', { roomId: targetId });
          socket.emit('go-live', { roomId: targetId });
        });
      } else {
        socket.emit('join-room', { roomId: targetId });
      }
    });

    socket.on('room-state', (d) => {
      setRoomInfo((prev) => ({
        ...(prev || {}),
        ...(d.room || {}),
        title: d.title || prev?.title,
        host: d.host || prev?.host,
        isLive: d.isLive,
        roomId: d.roomId || prev?.roomId,
      }));
      setRoomParticipants(d.participants || []);
      if (d.recentMessages) setChatMessages(d.recentMessages);
    });

    socket.on('peer-offer-needed', async (payload) => {
      const peerId = payload.peerId || payload.newPeerSocketId;
      if (!peerId) return;
      const pc = createPeer(peerId);
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      socket.emit('webrtc-offer', { roomId: targetId, to: peerId, targetSocketId: peerId, offer });
    });

    socket.on('webrtc-offer', async (payload) => {
      const from = payload.from || payload.fromSocketId;
      if (!from || !payload.offer) return;
      const pc = createPeer(from);
      await pc.setRemoteDescription(new RTCSessionDescription(payload.offer));
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      socket.emit('webrtc-answer', { roomId: targetId, to: from, targetSocketId: from, answer });
    });

    socket.on('webrtc-answer', async (payload) => {
      const from = payload.from || payload.fromSocketId;
      const pc = peersRef.current.get(from);
      if (pc && payload.answer) await pc.setRemoteDescription(new RTCSessionDescription(payload.answer));
    });

    socket.on('webrtc-ice-candidate', async (payload) => {
      const from = payload.from || payload.fromSocketId;
      const pc = peersRef.current.get(from);
      if (pc && payload.candidate) {
        try { await pc.addIceCandidate(new RTCIceCandidate(payload.candidate)); } catch {}
      }
    });

    socket.on('participant-joined', (p) => {
      const person = p.participant || p;
      setRoomParticipants((prev) => prev.find((x) => x.socketId === person.socketId) ? prev : [...prev, person]);
      addSysMsg(`${person.name || 'Someone'} joined`);
    });

    socket.on('participant-left', ({ socketId, name }) => {
      setRoomParticipants((prev) => prev.filter((p) => p.socketId !== socketId));
      setRemoteStreams((prev) => { const n = new Map(prev); n.delete(socketId); return n; });
      const pc = peersRef.current.get(socketId);
      if (pc) { pc.close(); peersRef.current.delete(socketId); }
      addSysMsg(`${name || 'Someone'} left`);
    });

    socket.on('chat-message', (msg) => {
      const incoming = {
        ...msg,
        id: Math.random(),
        sender: msg.sender || msg.name,
        text: msg.text || msg.content,
      };
      setChatMessages((prev) => [...prev, incoming]);
      if (!incoming.isLocal) setUnreadChat((n) => n + 1);
    });
    socket.on('reaction', ({ emoji }) => setIncomingReaction({ emoji, at: Date.now() }));
    socket.on('hand-raised', ({ name, socketId }) => {
      addSysMsg(`✋ ${name || 'Someone'} raised their hand`);
      if (socketId) setRaisedHands((p) => ({ ...p, [socketId]: true }));
    });
    socket.on('hand-lowered', ({ socketId }) => {
      if (socketId) setRaisedHands((p) => ({ ...p, [socketId]: false }));
    });
    socket.on('error', ({ message }) => setJoinError(message || 'Live room error'));
    socket.on('room-ended', () => { addSysMsg('⚠️ Session ended'); setMeetingEnded(true); setTimeout(leaveRoom, 2500); });
    socket.on('connect_error', (e) => setJoinError(e.message || 'Could not connect to the meeting'));

    setPhase('live');
  };

  const leaveRoom = useCallback(() => { cleanup(); navigate('/qoretalks/live'); }, [navigate]);

  const cleanup = () => {
    socketRef.current?.emit('leave-room', { roomId: roomIdParam || roomInfo?.roomId });
    socketRef.current?.disconnect();
    socketRef.current = null;
    peersRef.current.forEach(pc => pc.close());
    peersRef.current.clear();
    localStreamRef.current?.getTracks().forEach((t) => t.stop());
    screenStreamRef.current?.getTracks().forEach((t) => t.stop());
    localStreamRef.current = null;
    screenStreamRef.current = null;
    cameraTrackRef.current = null;
    setLocalStream(null);
    setRemoteStreams(new Map());
    setRoomParticipants([]);
    setIsSharing(false);
    setUnreadChat(0);
  };

  const toggleMic = () => {
    localStreamRef.current?.getAudioTracks().forEach(t => { t.enabled = !t.enabled; });
    setIsMicOn(p => !p);
  };

  const toggleCamera = () => {
    localStreamRef.current?.getVideoTracks().forEach((t) => { t.enabled = !t.enabled; });
    setIsCameraOn((p) => !p);
  };

  const stopShare = () => {
    screenStreamRef.current?.getTracks().forEach((t) => t.stop());
    screenStreamRef.current = null;
    const cam = cameraTrackRef.current;
    const stream = localStreamRef.current || new MediaStream();
    stream.getVideoTracks().forEach((t) => stream.removeTrack(t));
    if (cam && cam.readyState === 'live') {
      stream.addTrack(cam);
      replacePeerTrack('video', cam);
    }
    localStreamRef.current = stream;
    setLocalStream(new MediaStream(stream.getTracks()));
    setIsSharing(false);
  };

  const toggleShare = async () => {
    if (isSharing) {
      stopShare();
      return;
    }
    try {
      const display = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
      const track = display.getVideoTracks()[0];
      if (!track) return;
      const currentCam = localStreamRef.current?.getVideoTracks()[0];
      if (currentCam && currentCam !== cameraTrackRef.current) cameraTrackRef.current = currentCam;
      screenStreamRef.current = display;
      const stream = localStreamRef.current || new MediaStream();
      stream.getVideoTracks().forEach((t) => stream.removeTrack(t));
      stream.addTrack(track);
      localStreamRef.current = stream;
      setLocalStream(new MediaStream(stream.getTracks()));
      replacePeerTrack('video', track);
      setIsSharing(true);
      track.onended = () => stopShare();
    } catch { /* user cancelled */ }
  };

  const selectDevice = async (kind, deviceId) => {
    try {
      const extra = await navigator.mediaDevices.getUserMedia(
        kind === 'audio' ? { audio: { deviceId: { exact: deviceId } } } : { video: { deviceId: { exact: deviceId } } }
      );
      const newTrack = extra.getTracks()[0];
      const stream = localStreamRef.current || new MediaStream();
      stream.getTracks().filter((t) => t.kind === newTrack.kind).forEach((t) => {
        stream.removeTrack(t);
        t.stop();
      });
      stream.addTrack(newTrack);
      localStreamRef.current = stream;
      if (kind === 'video') {
        cameraTrackRef.current = newTrack;
        if (!isSharing) replacePeerTrack('video', newTrack);
      } else {
        replacePeerTrack('audio', newTrack);
      }
      setLocalStream(new MediaStream(stream.getTracks()));
      setSelectedDevices((p) => ({ ...p, [kind]: deviceId }));
    } catch (err) {
      setToast(err.message || 'Could not switch device');
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) document.documentElement.requestFullscreen?.();
    else document.exitFullscreen?.();
  };

  const sendMessage = (e) => {
    e.preventDefault();
    if (!chatInput.trim()) return;
    const msg = { id: Math.random(), text: chatInput.trim(), sender: user?.name || 'You', isLocal: true, timestamp: new Date().toISOString() };
    setChatMessages(prev => [...prev, msg]);
    socketRef.current?.emit('chat-message', {
      roomId: roomIdParam || roomInfo?.roomId,
      text: chatInput.trim(),
      content: chatInput.trim(),
    });
    setChatInput('');
  };

  const addSysMsg = (text) => setChatMessages(prev => [...prev, { id: Math.random(), text, isSystem: true, timestamp: new Date().toISOString() }]);

  const sendReaction = (emoji) => {
    socketRef.current?.emit('reaction', { roomId: roomIdParam || roomInfo?.roomId, emoji });
  };

  const toggleHand = () => {
    const next = !handRaised;
    setHandRaised(next);
    socketRef.current?.emit(next ? 'raise-hand' : 'lower-hand', { roomId: roomIdParam || roomInfo?.roomId });
  };

  const createRoom = async () => {
    if (!createForm.title.trim()) return;
    if (!token) {
      setJoinError('Please log in as host to start a live session.');
      return;
    }
    setCreateLoading(true);
    setJoinError('');
    try {
      const meeting = await createLiveMeeting(
        { title: createForm.title.trim(), description: createForm.description },
        token
      );
      const id = meeting.roomId;
      setRoomInfo({
        ...meeting,
        _id: id,
        host: meeting.host || { name: user?.name || user?.firstName || 'You' },
      });
      navigate(`/qoretalks/live/${id}?host=1`);
    } catch (err) {
      setJoinError(err.message || 'Could not create meeting');
    } finally {
      setCreateLoading(false);
    }
  };

  const InviteBar = ({ compact }) => {
    const id = (roomInfo?.roomId || roomIdParam || '').toUpperCase();
    if (!id) return null;
    const url = meetingShareUrl(id);
    return (
      <div className="qt-invite" style={{
        background: '#0f172a',
        border: '1px solid rgba(148,163,184,0.16)',
        borderRadius: 12,
        padding: compact ? '0.65rem 0.85rem' : '1rem',
        margin: compact ? 0 : '0 0 1.25rem',
        textAlign: 'left',
      }}>
        {!compact && (
          <p style={{ margin: '0 0 0.45rem', fontSize: '0.78rem', color: '#94a3b8', fontWeight: 700, letterSpacing: '0.04em' }}>
            SHARE MEETING
          </p>
        )}
        <p style={{ margin: '0 0 0.55rem', fontSize: compact ? '0.8rem' : '0.9rem', color: '#e2e8f0', wordBreak: 'break-all' }}>
          {url}
        </p>
        <div style={{ display: 'flex', gap: '0.45rem', flexWrap: 'wrap' }}>
          <button type="button" className="qt-btn qt-btn--rose qt-btn--sm" onClick={() => copyText(url, 'link')}>
            {copied === 'link' ? <Check sx={{ fontSize: 14 }} /> : <ContentCopy sx={{ fontSize: 14 }} />}
            {copied === 'link' ? 'Copied' : 'Copy invite link'}
          </button>
          <button type="button" className="qt-btn qt-btn--ghost qt-btn--sm" onClick={() => copyText(id, 'id')}>
            {copied === 'id' ? <Check sx={{ fontSize: 14 }} /> : <LinkIcon sx={{ fontSize: 14 }} />}
            {copied === 'id' ? 'Copied' : `ID ${id}`}
          </button>
        </div>
      </div>
    );
  };

  if (phase === 'ended') {
    return (
      <QoreTalksShell activeTab="live" mainClass="qt-main--narrow">
        <h2 style={{ margin: '0 0 0.5rem' }}>This meeting has ended</h2>
        <p style={{ color: '#94a3b8' }}>{roomInfo?.title || 'Live session'} is no longer available.</p>
        <button type="button" className="qt-btn qt-btn--primary" onClick={() => navigate('/qoretalks/live')}>
          Back to live rooms
        </button>
      </QoreTalksShell>
    );
  }

  // ─── RENDER BROWSE ────────────────────────────────────────────────────────
  if (phase === 'browse') return (
    <QoreTalksShell
      activeTab="live"
      hero={{
        badge: t('qoreTalks.liveHeroBadge'),
        title: t('qoreTalks.liveHeroTitle'),
        titleAccent: t('qoreTalks.liveHeroAccent'),
        subtitle: t('qoreTalks.liveHeroDesc'),
        actions: (isAdmin || isInstructor) ? (
          <button type="button" className="qt-btn qt-btn--rose" onClick={() => setPhase('create')}>
            <RadioButtonChecked sx={{ fontSize: 18 }} /> {t('qoreTalks.goLive')}
          </button>
        ) : null,
      }}
      mainClass="qt-main--narrow"
    >
      {roomsLoading ? (
        <div className="qt-loading" style={{ minHeight: 280 }}>
          <div className="qt-spinner" />
        </div>
      ) : liveRooms.length === 0 ? (
        <QtEmptyState
          variant="live"
          actions={
            (isAdmin || isInstructor) ? (
              <button type="button" className="qt-btn qt-btn--rose" onClick={() => setPhase('create')}>
                {t('qoreTalks.startSession')}
              </button>
            ) : null
          }
        />
      ) : (
        <div className="qt-live-grid qt-motion-grid">
          {liveRooms.map((room) => (
            <motion.div key={room._id} whileHover={{ y: -4 }} transition={{ duration: 0.2 }}>
              <LiveRoomCard
                room={room}
                onJoin={(r) => navigate(`/qoretalks/live/${(r.roomId || r._id || '').toUpperCase()}`)}
              />
            </motion.div>
          ))}
        </div>
      )}
    </QoreTalksShell>
  );

  // ─── RENDER CREATE ────────────────────────────────────────────────────────
  if (phase === 'create') return (
    <QoreTalksShell activeTab="live" mainClass="qt-main--narrow">
      <button
        type="button"
        className="qt-detail-back"
        onClick={() => { setPhase('browse'); fetchLiveRoomsList(); }}
      >
        ← {t('qoreTalks.backToRooms')}
      </button>
      <h2 style={{ margin: '0 0 0.35rem', fontSize: '1.5rem', fontWeight: 800 }}>{t('qoreTalks.startLiveRoom')}</h2>
      <p style={{ margin: '0 0 1.5rem', color: '#94a3b8', fontSize: '0.9rem' }}>
        Creates a Zoom-style meeting with a shareable link. Guests can wait in the lobby before you start.
      </p>
      <div className="qt-form" style={{ maxWidth: '100%' }}>
        <label style={{ fontSize: '0.8rem', color: '#94a3b8' }}>{t('qoreTalks.roomTitle')}</label>
        <input
          className="qt-input"
          placeholder="e.g. AI & Machine Learning Deep Dive"
          value={createForm.title}
          onChange={(e) => setCreateForm((p) => ({ ...p, title: e.target.value }))}
        />
        <label style={{ fontSize: '0.8rem', color: '#94a3b8' }}>{t('qoreTalks.roomDesc')}</label>
        <textarea
          className="qt-textarea"
          rows={3}
          placeholder="What will you discuss today?"
          value={createForm.description}
          onChange={(e) => setCreateForm((p) => ({ ...p, description: e.target.value }))}
        />
        {joinError && <p style={{ color: '#f87171', fontSize: '0.8rem', margin: 0 }}>{joinError}</p>}
        <div style={{ display: 'flex', gap: '0.65rem' }}>
          <button
            type="button"
            className="qt-btn qt-btn--ghost"
            style={{ flex: 1 }}
            onClick={() => { setPhase('browse'); fetchLiveRoomsList(); }}
          >
            {t('qoreTalks.cancel')}
          </button>
          <button
            type="button"
            className="qt-btn qt-btn--rose"
            style={{ flex: 2 }}
            onClick={createRoom}
            disabled={!createForm.title.trim() || createLoading}
          >
            {createLoading ? t('qoreTalks.creating') : t('qoreTalks.goLive')}
          </button>
        </div>
      </div>
    </QoreTalksShell>
  );

  // ─── RENDER PRE-CALL ──────────────────────────────────────────────────────
  if (phase === 'precall') return (
    <QoreTalksShell activeTab="live" hideNav>
      <button
        type="button"
        className="qt-detail-back"
        onClick={() => { cleanup(); fetchLiveRoomsList(); }}
      >
        ← {t('qoreTalks.backToRooms')}
      </button>
      <div className="ls-lobby">
        <div>
          <div className="ls-lobby__preview" style={{ height: 340 }}>
            <VideoTile fill stream={localStream} label={user?.name || user?.firstName || guestName || 'You'} isMuted={!isMicOn} isCameraOff={!isCameraOn} isLocal />
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '0.65rem', marginTop: '0.9rem' }}>
            <button type="button" className={`qt-btn ${isMicOn ? 'qt-btn--ghost' : 'qt-btn--rose'}`} onClick={toggleMic}>
              {isMicOn ? 'Mute' : 'Unmute'}
            </button>
            <button type="button" className={`qt-btn ${isCameraOn ? 'qt-btn--ghost' : 'qt-btn--rose'}`} onClick={toggleCamera}>
              {isCameraOn ? 'Camera off' : 'Camera on'}
            </button>
          </div>
        </div>
        <div className="ls-lobby__side">
          <h2>{roomInfo?.title || t('qoreTalks.liveLabel')}</h2>
          <p>
            Hosted by {roomInfo?.host?.name || 'Host'}
            {roomInfo?.status === 'waiting' ? ' · Waiting room is open' : ` · ${roomInfo?.participantCount || 0} already in`}
          </p>
          <InviteBar />
          {!token && (
            <div style={{ marginBottom: '0.9rem' }}>
              <label style={{ fontSize: '0.8rem', color: '#94a3b8' }}>Your name</label>
              <input
                className="qt-input"
                placeholder="Shown to everyone in the call"
                value={guestName}
                onChange={(e) => setGuestName(e.target.value)}
              />
            </div>
          )}
          {joinError && <p style={{ color: '#f87171' }}>{joinError}</p>}
          <button type="button" className="qt-btn qt-btn--primary qt-btn--lg" onClick={joinRoom} disabled={!token && !guestName.trim()} style={{ width: '100%' }}>
            {isHostParam || isAdmin || isInstructor ? 'Start meeting' : 'Join now'}
          </button>
        </div>
      </div>
    </QoreTalksShell>
  );

  // ─── RENDER LIVE ROOM ─────────────────────────────────────────────────────
  const myId = socketRef.current?.id;
  const hostId = roomInfo?.host?.userId;
  const tiles = [
    {
      id: 'local',
      stream: localStream,
      label: user?.name || user?.firstName || guestName || 'You',
      isLocal: true,
      isMuted: !isMicOn,
      isCameraOff: !isCameraOn && !isSharing,
      isHost: isHostParam || isAdmin || isInstructor,
      isScreenShare: isSharing,
      handRaised,
    },
    ...roomParticipants.filter((p) => p.socketId !== myId).map((p) => ({
      id: p.socketId,
      stream: remoteStreams.get(p.socketId) || null,
      label: p.name || 'Participant',
      isLocal: false,
      isMuted: p.isMuted || false,
      isCameraOff: p.isCameraOff || false,
      isHost: hostId && p.userId && String(p.userId) === String(hostId),
      isScreenShare: false,
      handRaised: !!raisedHands[p.socketId],
    })),
  ];

  return (
    <LiveStudio
      roomInfo={roomInfo}
      tiles={tiles}
      chatMessages={chatMessages}
      chatInput={chatInput}
      onChatInput={setChatInput}
      onSendChat={sendMessage}
      isMicOn={isMicOn}
      isCameraOn={isCameraOn}
      isSharing={isSharing}
      handRaised={handRaised}
      unreadChat={unreadChat}
      isHost={isHostParam || isAdmin || isInstructor}
      shareUrl={meetingShareUrl()}
      meetingId={(roomInfo?.roomId || roomIdParam || '').toUpperCase()}
      copied={copied}
      toast={toast}
      devices={devices}
      selectedDevices={selectedDevices}
      onSelectDevice={selectDevice}
      onToggleMic={toggleMic}
      onToggleCam={toggleCamera}
      onToggleShare={toggleShare}
      onToggleHand={toggleHand}
      onReaction={sendReaction}
      onCopyLink={() => copyText(meetingShareUrl(), 'link')}
      onCopyId={() => copyText((roomInfo?.roomId || roomIdParam || '').toUpperCase(), 'id')}
      onLeave={leaveRoom}
      onEnd={() => {
        socketRef.current?.emit('end-room', { roomId: roomIdParam || roomInfo?.roomId });
        leaveRoom();
      }}
      onFullscreen={toggleFullscreen}
      onChatOpen={() => setUnreadChat(0)}
      incomingReaction={incomingReaction}
    />
  );
};

const DEMO_ROOMS = [
  { _id: 'demo-1', title: 'AI & Machine Learning Live', host: { name: 'Dr. Sarah Johnson' }, participantCount: 42, isLive: true, category: 'AI' },
  { _id: 'demo-2', title: 'Web3 Deep Dive Session', host: { name: 'Alex Morgan' }, participantCount: 18, isLive: true, category: 'Web3' },
  { _id: 'demo-3', title: 'Career Growth in Tech Q&A', host: { name: 'Lisa Rodriguez' }, participantCount: 31, isLive: true, category: 'Career' },
];

export default LiveShow;
